import { Link } from '../../components/SmartLink';
import { useState } from 'react';
import {
  CheckCircle, Warning, MapPin, WhatsappLogo,
  Clock, Globe, ArrowRight, Shield, Airplane,
  Users, SealCheck, CaretDown, CalendarBlank,
  FileText, Lightbulb, Info, Star, Buildings, CurrencyDollar,
} from '@phosphor-icons/react';
import VisaDetailLayout from '../../components/visas/VisaDetailLayout';
import { VISA_BY_ID } from '../../data/visaData';
import { buildWAUrl } from '../../lib/whatsapp';

const WA_URL = buildWAUrl(
  'Hi Big Bird Travel, I am a UAE resident and need the Saudi multiple-entry visa. My nationality is ____ and my UAE residence is valid. Please check my eligibility and share the current price.'
);

const SUMMARY_CARDS = [
  { label: 'Visa Type', value: '1-Year Multi-Entry', sub: 'Tourism, business, family & Umrah', Icon: FileText },
  { label: 'Processing Time', value: '1 – 3 working days', sub: 'Often within 24 hours', Icon: Clock },
  { label: 'Stay Per Visit', value: 'Up to 90 days', sub: 'Multiple entries in the year', Icon: CalendarBlank },
  { label: 'Who Can Apply', value: 'All UAE residents', sub: 'Indian, Pakistani, Filipino & more', Icon: Users },
  { label: 'Apply From', value: 'Abu Dhabi / online', sub: 'No embassy visit — fully online', Icon: MapPin },
];

const GUIDE_FOR = [
  { Icon: Globe,     label: 'Indian, Pakistani & Filipino Residents' },
  { Icon: Star,      label: 'Umrah Pilgrims from the UAE' },
  { Icon: Buildings, label: 'Business Visitors to Riyadh & Jeddah' },
  { Icon: Users,     label: 'Families Visiting Relatives' },
  { Icon: Airplane,  label: 'Tourists — AlUla, Diriyah, Jeddah' },
  { Icon: MapPin,     label: 'Egyptian, Bangladeshi & Western Passports' },
];

const NATIONALITY_TABLE = [
  { nationality: 'Indian (UAE resident)',        short: 'Indian',             route: '1-Year Multi-Entry eVisa', stay: '90 days/visit', fee: '~AED 640', badge: 'blue' },
  { nationality: 'Pakistani (UAE resident)',     short: 'Pakistani',          route: '1-Year Multi-Entry eVisa', stay: '90 days/visit', fee: '~AED 640', badge: 'blue' },
  { nationality: 'Filipino (UAE resident)',      short: 'Filipino',           route: '1-Year Multi-Entry eVisa', stay: '90 days/visit', fee: '~AED 640', badge: 'blue' },
  { nationality: 'Egyptian / Bangladeshi (UAE resident)', short: 'Egyptian or Bangladeshi', route: '1-Year Multi-Entry eVisa', stay: '90 days/visit', fee: '~AED 640', badge: 'blue' },
  { nationality: 'British / American / EU',      short: 'British/American/EU', route: 'Tourist eVisa (also multi-entry)', stay: '90 days/visit', fee: '~AED 640', badge: 'green' },
  { nationality: 'GCC citizens',                 short: 'GCC',                route: 'Visa-free / eGate',       stay: 'Varies',       fee: 'Free',      badge: 'navy' },
  { nationality: 'Other nationalities',          short: null,                 route: 'Check with us',           stay: 'Varies',       fee: 'Varies',    badge: 'gray' },
];

function nationalityWAUrl(short: string): string {
  const article = /^[AEIOU]/.test(short) ? 'an' : 'a';
  return buildWAUrl(
    `Hi Big Bird Travel, I am ${article} ${short} passport holder and UAE resident. I need the Saudi multiple-entry visa — please confirm my eligibility and the current price.`
  );
}

const HIDDEN_FACTS = [
  {
    Icon: Shield,
    title: 'The meningitis (ACWY) vaccine is checked on arrival',
    desc: 'Saudi Arabia requires a valid meningococcal ACWY vaccination certificate for entry — and it is enforced at the airport, especially for Umrah. The certificate must usually be issued at least 10 days before travel and be within its validity window. We tell you exactly what is needed before you fly so you are never turned back.',
  },
  {
    Icon: CalendarBlank,
    title: 'Your UAE residence must have enough validity',
    desc: 'This visa route is unlocked by your UAE residence permit, so it generally needs at least 3 months of remaining validity when you apply. If your residence is close to expiry, we check eligibility first — rather than have the application rejected after you have paid.',
  },
  {
    Icon: CurrencyDollar,
    title: 'Medical insurance is built into the fee',
    desc: 'The Saudi eVisa now includes mandatory medical insurance inside the application, which is why the total is around AED 640–650 rather than just the visa fee. We handle the insurance step for you so the policy is valid and accepted.',
  },
  {
    Icon: Star,
    title: 'Umrah on the eVisa vs a dedicated Umrah visa',
    desc: 'You can perform Umrah on the 1-year multiple-entry eVisa (90 days per visit) — convenient if you also travel for tourism or business. A separate dedicated Umrah visa also exists (shorter stay). We advise which route fits your plans and budget, and package the whole journey.',
  },
  {
    Icon: Users,
    title: 'Women can travel for Umrah without a mahram',
    desc: 'Saudi Arabia allows women to perform Umrah without a male guardian (mahram). Female UAE residents can apply for the visa and travel independently or in a group. We arrange group departures and women-friendly hotel options near the Haram.',
  },
  {
    Icon: Airplane,
    title: 'One visa, many cities — plan the whole trip',
    desc: 'The multiple-entry visa lets you combine Makkah and Madinah with Jeddah, Riyadh, AlUla or Diriyah across the year. We book flights from Abu Dhabi (AUH) and Dubai (DXB), hotels near the Haramain or in the cities, and transfers — so it is all handled in one place.',
  },
];

const SAUDI_SERVICES = [
  { label: 'Umrah Packages',        to: '/umrah',        desc: 'Bus & air from the UAE' },
  { label: 'Flights to Saudi',      to: '/flights',      desc: 'Jeddah, Madinah & Riyadh' },
  { label: 'Hotel Booking',         to: '/hotels',       desc: 'Stays near the Haramain' },
  { label: 'Travel Insurance',      to: '/insurance',    desc: 'Saudi-approved cover' },
  { label: 'All Visa Services',     to: '/visas',        desc: 'UAE, Schengen, UK, USA & more' },
  { label: 'Contact Our Team',      to: '/contact',      desc: 'Mussafah, Abu Dhabi office' },
];

const SERVICE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Saudi Multiple-Entry Visa Assistance for UAE Residents',
  description: 'Saudi Arabia 1-year multiple-entry eVisa assistance for UAE residents of all nationalities — tourism, business, family and Umrah. Applied online from Abu Dhabi and Dubai. Based in Mussafah, Abu Dhabi.',
  url: 'https://bigbirdtravel.com/visas/saudi-visa',
  provider: {
    '@type': 'TravelAgency',
    name: 'Big Bird Travel & Tourism',
    url: 'https://bigbirdtravel.com',
    telephone: '+971561789642',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Al Sawari Street 5, Building No-21, Office No-4, M-13',
      addressLocality: 'Mussafah',
      addressRegion: 'Abu Dhabi',
      addressCountry: 'AE',
    },
    email: 'info@bigbirdtravel.com',
    openingHours: 'Mo-Sa 09:00-18:00',
  },
  areaServed: { '@type': 'Country', name: 'United Arab Emirates' },
  serviceType: 'Saudi Visa Assistance',
  termsOfService: 'https://bigbirdtravel.com/terms',
};

function ExpandableTip({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#C3DCF5] rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left bg-[#F5FAFF] hover:bg-[#EAF7FF] transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2.5">
          <Lightbulb size={14} weight="fill" className="text-[#0068B3] shrink-0" />
          <span className="font-display font-black text-[#071126] text-[13px] leading-tight">{title}</span>
        </div>
        <CaretDown size={14} weight="bold" className={`text-[#0068B3] shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 py-4 bg-white text-[13px] text-gray-600 leading-relaxed border-t border-[#C3DCF5]">
          {children}
        </div>
      )}
    </div>
  );
}

function AIAnswerBox({ question, answer }: { question: string; answer: string }) {
  return (
    <div className="rounded-2xl border-l-4 border-[#0096FF] bg-[#F0F8FF] px-5 py-4 shadow-sm">
      <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#0068B3] mb-1.5 flex items-center gap-1.5">
        <Info size={12} weight="fill" /> Quick Answer · Optimized for AI Overviews
      </p>
      <p className="font-display font-black text-[#071126] text-[14px] mb-2">{question}</p>
      <p className="text-[13px] text-gray-600 leading-relaxed">{answer}</p>
    </div>
  );
}

function SaudiVisaExtraSections() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(SERVICE_SCHEMA) }} />

      {/* 1. At-a-Glance */}
      <section className="py-10 px-6 lg:px-8 bg-[#F5FAFF] border-b border-[#D8EAF7]">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-4">Saudi Visa for UAE Residents — At a Glance</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {SUMMARY_CARDS.map(({ label, value, sub, Icon }) => (
              <div key={label} className="rounded-2xl border border-[#C3DCF5] bg-white p-4 shadow-sm flex flex-col gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EAF7FF] border border-[#C3DCF5]">
                  <Icon size={16} weight="fill" className="text-[#0068B3]" />
                </span>
                <p className="text-[10px] font-black uppercase tracking-wider text-[#0068B3]">{label}</p>
                <p className="font-display font-black text-[#071126] text-[14px] leading-tight">{value}</p>
                <p className="text-[11px] text-gray-400 leading-tight">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2. Who this guide is for */}
      <section className="py-10 px-6 lg:px-8 bg-white border-b border-gray-50">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 items-start">
          <div className="shrink-0 lg:w-60">
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-2 block">About This Guide</span>
            <h2 className="font-display font-black text-[#071126] text-[20px] leading-tight">Who this is for</h2>
            <p className="text-gray-500 text-[13px] mt-2 leading-relaxed">
              For UAE residents of every nationality planning Saudi Arabia — for Umrah, tourism, business or family — from Abu Dhabi and Dubai.
            </p>
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {GUIDE_FOR.map(({ Icon, label }) => (
              <div key={label} className="flex items-center gap-3 rounded-xl border border-[#D8EAF7] bg-[#F5FAFF] px-4 py-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EAF7FF] border border-[#C3DCF5]">
                  <Icon size={14} weight="fill" className="text-[#0068B3]" />
                </span>
                <span className="text-[13px] font-semibold text-[#071126]">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. AI Answer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 space-y-4">
        <AIAnswerBox
          question="Can Indian and Pakistani UAE residents get a Saudi visa in 2026?"
          answer="Yes. Since late 2025, Saudi Arabia offers a 1-year, multiple-entry eVisa to UAE residents of almost every nationality — including Indian, Pakistani, Filipino, Egyptian and Bangladeshi passport holders — using a valid UAE residence permit. It is applied online with no embassy visit, allows stays of up to 90 days per visit, and covers tourism, business, family visits and Umrah. Big Bird Travel, based in Mussafah, Abu Dhabi, confirms your eligibility and prepares the application, including the mandatory medical insurance and meningitis vaccination requirement."
        />
        <AIAnswerBox
          question="Can I perform Umrah on the Saudi multiple-entry visa?"
          answer="Yes. The 1-year multiple-entry tourist eVisa allows you to perform Umrah, alongside tourism, business and family visits, with up to 90 days per visit. You must hold a valid meningitis (ACWY) vaccination certificate. A separate dedicated Umrah visa also exists with a shorter stay. Big Bird Travel arranges the visa, Jeddah or Madinah flights, and hotels near the Haram as one Umrah package."
        />
      </div>

      {/* 4. Nationality table */}
      <section className="py-12 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">Eligibility by Nationality</span>
          <h2 className="font-display font-black text-[#071126] mb-2" style={{ fontSize: 'clamp(1.3rem, 2vw, 1.65rem)' }}>
            Saudi visa routes for UAE residents by passport — 2026
          </h2>
          <p className="text-gray-500 text-[13px] mb-6 max-w-2xl">
            Fees below include the government eVisa fee and mandatory insurance and are indicative — they are set by Saudi authorities and can change. Tap "Check" on your nationality to confirm your exact case with us.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-[#D8EAF7] shadow-sm">
            <table className="w-full text-left text-[13px] min-w-[650px]">
              <thead>
                <tr className="bg-[#071126] text-white">
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider rounded-tl-2xl">Passport (UAE Resident)</th>
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider">Route</th>
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider">Stay</th>
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider">From (approx)</th>
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider rounded-tr-2xl text-center">Your Case</th>
                </tr>
              </thead>
              <tbody>
                {NATIONALITY_TABLE.map((row, i) => (
                  <tr key={row.nationality} className={`border-t border-[#E8F3FC] ${i % 2 === 0 ? 'bg-white' : 'bg-[#F5FAFF]'}`}>
                    <td className="px-5 py-3.5 font-semibold text-[#071126]">{row.nationality}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide whitespace-nowrap ${
                        row.badge === 'green' ? 'bg-green-100 text-green-800' :
                        row.badge === 'blue'  ? 'bg-[#EAF7FF] text-[#0068B3]' :
                        row.badge === 'navy'  ? 'bg-[#071126] text-white' :
                                                'bg-gray-100 text-gray-600'
                      }`}>{row.route}</span>
                    </td>
                    <td className="px-5 py-3.5 text-gray-600">{row.stay}</td>
                    <td className="px-5 py-3.5 font-bold text-[#0068B3]">{row.fee}</td>
                    <td className="px-5 py-3.5 text-center">
                      <a
                        href={row.short ? nationalityWAUrl(row.short) : WA_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#0F7A35] text-white text-[11px] font-black px-3 py-1.5 hover:bg-[#0a6029] transition-colors whitespace-nowrap"
                      >
                        <WhatsappLogo size={12} weight="fill" /> Check
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-[11px] text-gray-400">
            * Indicative 2026 figures incl. insurance — final price confirmed per case on WhatsApp.
          </p>
        </div>
      </section>

      {/* 5. Know before you apply */}
      <section className="py-14 px-6 lg:px-8 bg-amber-50 border-y border-amber-100">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
          <div className="lg:col-span-2">
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-700 mb-3 block">⚠ Know Before You Apply</span>
            <h2 className="font-display font-black text-[#071126] mb-4" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
              The details that catch UAE travellers out at the Saudi border
            </h2>
            <p className="text-gray-700 text-[15px] leading-relaxed mb-4">
              The most common surprise is the <strong>meningitis (ACWY) vaccination</strong> — it is required for entry, checked at the airport, and must be done in advance. The second is <strong>UAE residence validity</strong> — the visa route depends on it, so it generally needs at least 3 months left.
            </p>
            <p className="text-gray-700 text-[15px] leading-relaxed mb-6">
              We flag both before you book, arrange the insurance inside the application, and confirm your exact route by nationality — so nothing goes wrong at the gate.
            </p>
            <p className="text-[12px] font-black uppercase tracking-widest text-amber-700 mb-3 flex items-center gap-1.5">
              <Lightbulb size={12} weight="fill" /> Expand for pro tips
            </p>
            <div className="space-y-2.5">
              <ExpandableTip title="Get the meningitis (ACWY) vaccine before you travel">
                Saudi Arabia requires a valid meningococcal ACWY certificate, usually issued at least 10 days before arrival and within its validity period. It is checked at the airport, especially for Umrah. Book the vaccination early at a UAE clinic and keep the certificate with your travel documents.
              </ExpandableTip>
              <ExpandableTip title="Check your UAE residence has enough validity">
                The eVisa is unlocked by your UAE residence permit and generally needs at least 3 months of remaining validity when you apply. If your residence is near expiry, renew it or check eligibility with us first — the application can be rejected otherwise.
              </ExpandableTip>
              <ExpandableTip title="Insurance is included — but must be valid">
                The eVisa fee (~AED 640–650) already includes mandatory Saudi medical insurance, purchased within the application. We complete this step correctly so the policy is accepted and covers your full stay.
              </ExpandableTip>
              <ExpandableTip title="Choose the right route for Umrah">
                You can perform Umrah on the 1-year multiple-entry eVisa (90 days/visit), which suits travellers who also visit for tourism or business. If you only want a short Umrah trip, a dedicated Umrah visa may fit better. Tell us your plan and we will advise the cheaper, cleaner route.
              </ExpandableTip>
            </div>
          </div>

          <div className="bg-white border border-amber-200 rounded-2xl p-6 shadow-sm lg:sticky lg:top-24">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle size={18} weight="fill" className="text-[#0068B3]" />
              <span className="font-black text-[12px] uppercase tracking-widest text-[#0068B3]">Quick Eligibility Check</span>
            </div>
            <ul className="space-y-3 mb-5">
              {[
                { text: 'Indian / Pakistani + UAE residence', result: '1-yr multi-entry eVisa', type: 'ok' },
                { text: 'Filipino / Egyptian / Bangladeshi', result: '1-yr multi-entry eVisa', type: 'ok' },
                { text: 'Going for Umrah', result: 'Allowed on this visa', type: 'ok' },
                { text: 'No meningitis (ACWY) vaccine', result: 'Get it before travel', type: 'warn' },
                { text: 'UAE residence near expiry', result: 'Check with us first', type: 'warn' },
              ].map(item => (
                <li key={item.text} className="flex items-start gap-2.5">
                  {item.type === 'ok' && <CheckCircle size={14} weight="fill" className="text-green-500 shrink-0 mt-0.5" />}
                  {item.type === 'warn' && <Warning size={14} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />}
                  <span className="text-[12px] text-gray-700"><strong>{item.text}</strong> → {item.result}</span>
                </li>
              ))}
            </ul>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full rounded-xl bg-[#0F7A35] text-white text-[13px] font-black py-3.5 hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={17} weight="fill" /> Check My Eligibility — Free
            </a>
            <p className="text-center text-[11px] text-gray-400 mt-2.5 leading-snug">
              Send your nationality &amp; UAE residence — honest answer, same day.
            </p>
          </div>
        </div>
      </section>

      {/* 6. What sites don't tell you */}
      <section className="relative overflow-hidden py-14 px-6 lg:px-8 bg-[#071126] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,150,255,0.10),transparent_55%)] pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#C9A227] mb-3 block">Authority Section</span>
          <h2 className="font-display font-black text-white mb-3" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
            What most Saudi-visa pages don't tell UAE residents
          </h2>
          <p className="text-blue-50/60 text-[14px] mb-8 max-w-2xl">
            The basics are everywhere. These are the details that decide whether your Saudi trip — or Umrah — goes smoothly.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {HIDDEN_FACTS.map(({ Icon, title, desc }) => (
              <div key={title} className="rounded-2xl border border-[#0096FF]/20 bg-white/[0.045] p-5 hover:border-[#0096FF]/40 hover:bg-white/[0.06] transition-all">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0096FF]/10 border border-[#0096FF]/20 mb-4">
                  <Icon size={17} weight="fill" className="text-[#0096FF]" />
                </span>
                <h3 className="font-display font-black text-white text-[13px] mb-2 leading-tight">{title}</h3>
                <p className="text-blue-50/60 text-[12px] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border border-[#0096FF]/25 bg-white/[0.04] p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <p className="font-display font-black text-white text-[15px] mb-1">Not sure about your eligibility or Umrah route?</p>
              <p className="text-blue-50/60 text-[13px]">Send your nationality, UAE residence status and travel purpose. We'll confirm the route and an honest price — usually within the hour.</p>
            </div>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-2 bg-[#0F7A35] text-white text-[13px] font-black px-5 py-3 rounded-xl hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={17} weight="fill" /> Ask on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* 7. How Big Bird helps */}
      <section className="relative overflow-hidden py-14 px-6 lg:px-8 bg-[#041024] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,150,255,0.14),transparent_50%)]" />
        <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0096FF] mb-3 block">Why Big Bird</span>
            <h2 className="font-display font-black text-white mb-4" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
              Saudi visa + Umrah, handled by one UAE team
            </h2>
            <p className="text-blue-50/85 text-[15px] italic leading-relaxed mb-5 border-l-2 border-[#0096FF] pl-4">
              "Send your nationality and UAE residence details. We'll confirm your Saudi visa route, sort the insurance and vaccine info, and can arrange the whole Umrah trip."
            </p>
            <p className="text-blue-50/70 text-[14px] leading-relaxed mb-4">
              We're a licensed travel agency in <strong className="text-white">Mussafah, Abu Dhabi</strong>. For Saudi visas we:
            </p>
            <ul className="space-y-3 mb-6">
              {[
                'Confirm your eligibility by nationality and UAE residence — no guesswork.',
                'Prepare the eVisa correctly, including the mandatory medical insurance step.',
                'Tell you the meningitis (ACWY) vaccine and document requirements upfront.',
                'Advise the best route for Umrah vs tourism and check every document before submission.',
                'Bundle Jeddah/Madinah/Riyadh flights, hotels near the Haram and full Umrah packages.',
              ].map(item => (
                <li key={item} className="flex items-start gap-3 text-[13px] text-blue-50/80">
                  <CheckCircle size={14} weight="fill" className="text-[#0096FF] shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#0F7A35] text-white text-[13px] font-black px-6 py-3.5 rounded-xl hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={19} weight="fill" /> WhatsApp Us — Same-Day Response
            </a>
          </div>
          <div className="space-y-4">
            <div className="bg-white/[0.05] border border-[#0096FF]/20 rounded-3xl p-6">
              <p className="text-[10px] font-black uppercase tracking-widest text-[#0096FF]/70 mb-4">Big Bird Travel — Agency Details</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {[
                  { label: 'Office',   value: 'Mussafah 13, Abu Dhabi' },
                  { label: 'WhatsApp', value: '+971 56 178 9642' },
                  { label: 'License',  value: 'ADRA CN-5285999' },
                  { label: 'Response', value: 'Same day' },
                  { label: 'Hours',    value: 'Mon–Sat 9am–6pm UAE' },
                  { label: 'Serves',   value: 'Abu Dhabi · Dubai · UAE' },
                ].map(item => (
                  <div key={item.label}>
                    <p className="text-[10px] font-black uppercase tracking-widest text-[#0096FF]/60 mb-0.5">{item.label}</p>
                    <p className="text-[13px] font-semibold text-white">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/[0.05] border border-[#0096FF]/20 rounded-2xl p-5">
              <div className="flex items-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (<Star key={i} size={12} weight="fill" className="text-[#C9A227]" />))}
                <span className="text-[11px] text-blue-50/50 ml-2">Verified customer</span>
              </div>
              <blockquote className="text-[13px] text-blue-50/80 italic leading-relaxed">
                "They sorted my 1-year Saudi visa as an Indian resident, reminded me about the meningitis vaccine, and booked my Umrah flights and hotel. Everything in one place."
              </blockquote>
              <p className="text-[11px] text-blue-50/40 mt-2">— Indian passport holder, Abu Dhabi resident</p>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Internal links — Saudi cluster */}
      <section className="py-12 px-6 lg:px-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">Plan Your Saudi Trip</span>
          <h2 className="font-display font-black text-[#071126] mb-6 text-[20px]">
            Visa sorted. Now plan your Umrah, flights and hotels.
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {SAUDI_SERVICES.map(({ label, to, desc }) => (
              <Link key={label} to={to} className="group rounded-2xl border border-[#D8EAF7] bg-[#F5FAFF] p-4 hover:border-[#0096FF]/50 hover:bg-[#EAF7FF] hover:-translate-y-0.5 transition-all flex flex-col gap-2">
                <ArrowRight size={15} weight="bold" className="text-[#0068B3] group-hover:translate-x-0.5 transition-transform" />
                <p className="font-display font-black text-[#071126] text-[13px] leading-tight">{label}</p>
                <p className="text-[11px] text-gray-400">{desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 9. E-E-A-T trust strip */}
      <section className="py-7 px-6 lg:px-8 bg-[#F5FAFF] border-b border-[#D8EAF7]">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-x-8 gap-y-3">
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><CalendarBlank size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Last Updated:</strong> September 2026</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><SealCheck size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Reviewed by:</strong> Big Bird Travel Visa Team</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><Shield size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">License:</strong> ADRA CN-5285999</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><MapPin size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Office:</strong> Mussafah 13, Abu Dhabi, UAE</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><WhatsappLogo size={13} weight="fill" className="text-green-600" /><span><strong className="text-[#071126]">WhatsApp:</strong> Same-day response</span></div>
        </div>
      </section>

      {/* 10. Areas we serve */}
      <section className="py-12 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            <div className="shrink-0">
              <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-2 block">Local Service</span>
              <h2 className="font-display font-black text-[#071126] text-[18px]">Areas We Serve</h2>
            </div>
            <div className="flex-1">
              <p className="text-gray-500 text-[14px] mb-4">
                We process Saudi visas and Umrah for UAE residents across <strong className="text-[#071126]">Abu Dhabi, Dubai, and the Northern Emirates</strong> — from our Mussafah office, with real one-on-one help.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Mussafah', 'Mohammed Bin Zayed City', 'Khalifa City', 'Al Nahyan', 'Abu Dhabi City', 'Al Ain', 'Dubai', 'Sharjah', 'Northern Emirates'].map(area => (
                  <span key={area} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EAF7FF] border border-[#C3DCF5] text-[12px] font-black text-[#0068B3]">
                    <MapPin size={11} weight="fill" />{area}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default function SaudiVisa() {
  return (
    <VisaDetailLayout
      visa={VISA_BY_ID['saudi-visa']}
      extraSections={<SaudiVisaExtraSections />}
    />
  );
}
